import type { GccDashboardData, GccEndStateRole, GccOutsourcingScenario } from '@shared/types'
import { GCC_SCENARIO_OFFSHORE_RATE as SCENARIO_OFFSHORE_RATE } from '@shared/gcc/combine'

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

const toNumber = (value: unknown): number => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

/**
 * Mirrors `normalizeGccEndStateRole` in @tn/core: truncated Step 4 output can
 * leave roles with missing skill lists or non-numeric headcounts, which would
 * otherwise show as NaN HC in the preview.
 */
export function normalizeGccRole(role: GccEndStateRole): GccEndStateRole {
  return {
    ...role,
    roleName: typeof role.roleName === 'string' ? role.roleName : '',
    headcountToday: toNumber(role.headcountToday),
    headcountEndState: toNumber(role.headcountEndState),
    headcountDelta: toNumber(role.headcountDelta),
    skillsIn: toStringArray(role.skillsIn),
    skillsOut: toStringArray(role.skillsOut),
    skillsNetNew: toStringArray(role.skillsNetNew),
    labourCostTodayUSD: toNumber(role.labourCostTodayUSD),
    labourCostEndStateUSD: toNumber(role.labourCostEndStateUSD),
  }
}

const normalizeRoles = (roles: unknown): GccEndStateRole[] =>
  Array.isArray(roles) ? roles.filter((r) => r && typeof r === 'object').map(normalizeGccRole) : []

export function computeRetainedRolesForScenario(
  gccData: GccDashboardData,
  scenario: GccOutsourcingScenario
): GccEndStateRole[] {
  const offshoreRate = SCENARIO_OFFSHORE_RATE[scenario]
  const retainedRoles = normalizeRoles(gccData.retainedPopulation?.endStateRoles)

  if (offshoreRate === 1.0) {
    return retainedRoles
  }

  const residualRate = 1 - offshoreRate
  const residualFromEligible = normalizeRoles(gccData.gccPopulation?.endStateRoles)
    .map((role) => {
      const endStateHeadcount = Math.round(role.headcountEndState * residualRate)
      return {
        ...role,
        segment: 'Retained' as const,
        headcountEndState: endStateHeadcount,
        headcountDelta: endStateHeadcount - role.headcountToday,
      }
    })
    .filter((r) => r.headcountEndState > 0)

  return [...retainedRoles, ...residualFromEligible]
}

/**
 * Build a synthetic JSON file representing the retained workforce for a given
 * outsourcing scenario. The base64-encoded output can be passed directly to the
 * inside-out pipeline upload slot (desktop) or previewed before "Run TN Analysis".
 */
export function buildGccSyntheticFile(
  gccData: GccDashboardData,
  scenario: GccOutsourcingScenario
): { base64: string; name: string } {
  const roles = computeRetainedRolesForScenario(gccData, scenario)
  const json = JSON.stringify({ scenario, roles }, null, 2)
  const base64 = btoa(Array.from(new TextEncoder().encode(json), (b) => String.fromCharCode(b)).join(''))
  return {
    base64,
    name: `gcc-retained-workforce-${scenario.toLowerCase()}.json`,
  }
}
